import {
    getChartCIsByCIClass,
    getStackedBarChartBasicConfig,
    getChartActiveClosed
} from 'containers/monitoring/config/charts';

import {invalidateMonitoringEvents} from 'modules/store/monitoringEvents';
import {invalidateMonitoringClientsIdClasses} from 'modules/store/monitoringClientsIdClasses';
import {invalidateMonitoringObjectsId} from 'modules/store/monitoringObjectsId';
import {invalidateMonitoringClientsId} from 'modules/store/monitoringClientsId';
import {invalidateMonitoringDaysBySeverity} from 'modules/store/monitoringDaysBySeverity';
import {invalidateMonitoringAlarms} from 'modules/store/monitoringAlarms';
import {invalidateMonitoringHost} from 'modules/store/monitoringHost';
import {invalidateMonitoringActiveClosedEvents} from 'modules/store/monitoringActiveClosedEvents';

const CREATE_HOURLY_PER_DAY_BY_SEVERITY = 'CREATE_HOURLY_PER_DAY_BY_SEVERITY';
const CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS = 'CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS';
const CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS_ACTIVE = 'CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS_ACTIVE';
const CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS_INACTIVE = 'CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS_INACTIVE';
const CREATE_TOP_EVENTS_BY_SEVERITY = 'CREATE_TOP_EVENTS_BY_SEVERITY';
const CREATE_TOP_10_CI_WITH_EVENTS = 'CREATE_TOP_10_CI_WITH_EVENTS';
const CREATE_TOP_10_CI_WITH_EVENTS_ACTIVE = 'CREATE_TOP_10_CI_WITH_EVENTS_ACTIVE';
const CREATE_TOP_10_CI_WITH_EVENTS_INACTIVE = 'CREATE_TOP_10_CI_WITH_EVENTS_INACTIVE';
const CREATE_CIS_BY_CI_CLASS = 'CREATE_CIS_BY_CI_CLASS';
const CREATE_ACTIVE_CLOSED_EVENTS = 'CREATE_ACTIVE_CLOSED_EVENTS';

const WARNING = '#53B7E8';
const MINOR = '#043776';
const MAJOR = '#FFC814';
const DANGER = '#F06930';
const CRITICAL = '#F68C1F';

const STATES = {
    CRITICAL,
    WARNING,
    MINOR,
    MAJOR,
    DANGER
};

export const invalidateMonitoring = () => dispatch => {
    dispatch(invalidateMonitoringEvents());
    dispatch(invalidateMonitoringClientsIdClasses());
    dispatch(invalidateMonitoringObjectsId());
    dispatch(invalidateMonitoringClientsId());
    dispatch(invalidateMonitoringDaysBySeverity());
    dispatch(invalidateMonitoringActiveClosedEvents());
};

export const invalidateEventsTable = () => dispatch => {
    dispatch(invalidateMonitoringHost());
    dispatch(invalidateMonitoringAlarms());
};

export const createHourlyPerDayBySeverity = (categories, series, yAxisTitle) => ({
    type: CREATE_HOURLY_PER_DAY_BY_SEVERITY,
    categories, series, yAxisTitle
});

export const createTop10ObjectClassWithEvents = (categories, series, yAxisTitle) => ({
    type: CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS,
    categories, series, yAxisTitle
});

export const createTop10ObjectClassWithEventsActive = (categories, series, yAxisTitle) => ({
    type: CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS_ACTIVE,
    categories, series, yAxisTitle
});

export const createTop10ObjectClassWithEventsInactive = (categories, series, yAxisTitle) => ({
    type: CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS_INACTIVE,
    categories, series, yAxisTitle
});

export const createTopEventsBySeverity = (series, total) => ({
    type: CREATE_TOP_EVENTS_BY_SEVERITY,
    series, total
});

export const createTop10CIWithEvents = (categories, series, yAxisTitle) => ({
    type: CREATE_TOP_10_CI_WITH_EVENTS,
    categories, series, yAxisTitle
});

export const createTop10CIWithEventsActive = (categories, series, yAxisTitle) => ({
    type: CREATE_TOP_10_CI_WITH_EVENTS_ACTIVE,
    categories, series, yAxisTitle
});

export const createTop10CIWithEventsInactive = (categories, series, yAxisTitle) => ({
    type: CREATE_TOP_10_CI_WITH_EVENTS_INACTIVE,
    categories, series, yAxisTitle
});

export const createCIsByCIClass = series => ({
    type: CREATE_CIS_BY_CI_CLASS,
    series
});

export const createActiveClosedEvents = series => ({
    type: CREATE_ACTIVE_CLOSED_EVENTS,
    series
});

const _createHourlyByDayGroupedBySeverity = (categories, series, yAxisTitle) => {
    const config = getStackedBarChartBasicConfig();

    return {
        ...config,
        series,
        xAxis: {
            ...config.xAxis,
            categories
        },
        yAxis: {
            ...config.yAxis,
            title: {
                text: yAxisTitle
            }
        }
    };
};

const _createTop10ObjectClassWithEvents = (categories, series, yAxisTitle) => {
    const config = getStackedBarChartBasicConfig();

    return {
        ...config,
        series,
        xAxis: {
            ...config.xAxis,
            categories
        },
        yAxis: {
            ...config.yAxis,
            title: {
                text: yAxisTitle
            }
        }
    };
};

const _createTop10ObjectClassWithEventsActive = (categories, series, yAxisTitle) => {
    const config = getStackedBarChartBasicConfig();

    return {
        ...config,
        series,
        xAxis: {
            ...config.xAxis,
            categories
        },
        yAxis: {
            ...config.yAxis,
            title: {
                text: yAxisTitle
            }
        }
    };
};

const _createTop10ObjectClassWithEventsInactive = (categories, series, yAxisTitle) => {
    const config = getStackedBarChartBasicConfig();

    return {
        ...config,
        series,
        xAxis: {
            ...config.xAxis,
            categories
        },
        yAxis: {
            ...config.yAxis,
            title: {
                text: yAxisTitle
            }
        }
    };
};

const _createTopEventsBySeverity = (series, title) => ({
    chart: {
        type: 'pie'
    },
    title: {
        text:          title/*`Total: ${title}`*/,
        verticalAlign: 'middle',
        // eslint-disable-next-line
        y:             -6,
        floating:      true,
        style:         {
            fontSize: '30px'
        }
    },
    plotOptions: {
        pie: {
            // eslint-disable-next-line
            fillOpacity: 0.4,
            dataLabels:  {
                enabled: false,
                format:  '{point.percentage:.1f} %'
            },
            showInLegend: true
        }
    },
    colors:  [STATES.CRITICAL, STATES.MAJOR, STATES.MINOR, STATES.WARNING],
    tooltip: {
        headerFormat: '',
        pointFormat:  '<span>{point.name}</span>: <b>{point.y}</b><br/>'
    },
    series: [
        {
            colorByPoint: true,
            innerSize:    '60%',
            data:         series
        }
    ]
});

const _createTop10CIWithEvents = (categories, series, yAxisTitle) => {
    const config = getStackedBarChartBasicConfig();

    return {
        ...config,
        series,
        xAxis: {
            ...config.xAxis,
            categories
        },
        yAxis: {
            ...config.yAxis,
            title: {
                text: yAxisTitle
            }
        }

    };
};

const _createTop10CIWithEventsActive = (categories, series, yAxisTitle) => {
    const config = getStackedBarChartBasicConfig();

    return {
        ...config,
        series,
        xAxis: {
            ...config.xAxis,
            categories
        },
        yAxis: {
            ...config.yAxis,
            title: {
                text: yAxisTitle
            }
        }

    };
};

const _createTop10CIWithEventsInactive = (categories, series, yAxisTitle) => {
    const config = getStackedBarChartBasicConfig();

    return {
        ...config,
        series,
        xAxis: {
            ...config.xAxis,
            categories
        },
        yAxis: {
            ...config.yAxis,
            title: {
                text: yAxisTitle
            }
        }

    };
};

const _createCIsByCIClass = series => getChartCIsByCIClass(series);

const _createActiveClosedEvents = series => getChartActiveClosed(series);

const _getDefaultState = () => ({
    hourlyPerDayBySeverity:     undefined,
    topEventsBySeverityConfig:  undefined,
    top10CIWithEventsConfig:    undefined,
    CIsBYCIClassConfig:         undefined,
    top10ObjectClassWithEvents: undefined
});

export const reducer = (state = _getDefaultState(), action = {}) => {
    switch (action.type) {
    case CREATE_HOURLY_PER_DAY_BY_SEVERITY:
        return {
            ...state,
            hourlyPerDayBySeverity: _createHourlyByDayGroupedBySeverity(action.categories, action.series, action.yAxisTitle)
        };
    case CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS:
        return {
            ...state,
            top10ObjectClassWithEvents: _createTop10ObjectClassWithEvents(action.categories, action.series, action.yAxisTitle)
        };
    case CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS_ACTIVE:
        return {
            ...state,
            top10ObjectClassWithEventsActive: _createTop10ObjectClassWithEventsActive(action.categories, action.series, action.yAxisTitle)
        };
    case CREATE_TOP_10_OBJECT_CLASS_WITH_EVENTS_INACTIVE:
        return {
            ...state,
            top10ObjectClassWithEventsInactive: _createTop10ObjectClassWithEventsInactive(action.categories, action.series, action.yAxisTitle)
        };
    case CREATE_TOP_EVENTS_BY_SEVERITY:
        return {
            ...state,
            topEventsBySeverityConfig: _createTopEventsBySeverity(action.series, action.total)
        };
    case CREATE_TOP_10_CI_WITH_EVENTS:
        return {
            ...state,
            top10CIWithEventsConfig: _createTop10CIWithEvents(action.categories, action.series, action.yAxisTitle)
        };
    case CREATE_TOP_10_CI_WITH_EVENTS_ACTIVE:
        return {
            ...state,
            top10CIWithEventsConfigActive: _createTop10CIWithEventsActive(action.categories, action.series, action.yAxisTitle)
        };
    case CREATE_TOP_10_CI_WITH_EVENTS_INACTIVE:
        return {
            ...state,
            top10CIWithEventsConfigInactive: _createTop10CIWithEventsInactive(action.categories, action.series, action.yAxisTitle)
        };
    case CREATE_CIS_BY_CI_CLASS:
        return {
            ...state,
            CIsBYCIClassConfig: _createCIsByCIClass(action.series)
        };
    case CREATE_ACTIVE_CLOSED_EVENTS:
        return {
            ...state,
            ActiveClosedEventsConfig: _createActiveClosedEvents(action.series)
        };
    default:
        return state;
    }


};



// WEBPACK FOOTER //
// ./src/modules/monitoring.js